<template>
  <div class="tg-page-root tg-viewport-page min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-white to-purple-50 px-4 py-12 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
    <div class="w-full max-w-sm">
      <!-- Logo/Title Section -->
      <div class="text-center mb-10">
        <div class="flex justify-center mb-4">
          <img src="@/assets/img/logo.png" alt="LeSIM" class="h-16 w-auto drop-shadow-sm" />
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-300">{{ $t('Login_Welcome') }}</p>
      </div>

      <!-- Login Form -->
      <div class="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-white/60 dark:border-gray-700/60 shadow-xl shadow-violet-100/50 dark:shadow-none p-6 space-y-5">
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('Login_Email') }}
          </label>
          <input
            v-model="email"
            type="email"
            :placeholder="$t('Login_Email_Placeholder')"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            :class="{ 'border-red-400 focus:ring-red-400 focus:border-red-400': emailError }"
          />
          <p v-if="emailError" class="text-red-500 text-xs mt-1">{{ emailError }}</p>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('Login_Password') }}
          </label>
          <div class="relative">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('Login_Password_Placeholder')"
              class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all pr-10 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              :class="{ 'border-red-400 focus:ring-red-400 focus:border-red-400': passwordError }"
              @keyup.enter="handleLogin"
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400 dark:text-gray-300 transition-colors"
            >
              <svg v-if="showPassword" class="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
              <svg v-else class="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>
          <p v-if="passwordError" class="text-red-500 text-xs mt-1">{{ passwordError }}</p>
        </div>

        <div class="flex items-center justify-between text-xs">
          <label class="flex items-center cursor-pointer">
            <input
              v-model="rememberMe"
              type="checkbox"
              class="w-3.5 h-3.5 rounded border-gray-300 text-brand focus:ring-1 focus:ring-brand accent-brand"
              style="accent-color: #8B5CF6;"
            />
            <span class="ml-2 text-gray-600 dark:text-gray-300">{{ $t('Login_Remember_Me') }}</span>
          </label>
          <button
            type="button"
            @click="goToForgotPassword"
            class="text-brand hover:text-brand/80 text-xs font-medium transition-colors"
          >
            {{ $t('Login_Forgot_Password') }}
          </button>
        </div>

        <button
          type="button"
          @click="handleLogin"
          :disabled="loading"
          class="w-full bg-brand text-white py-2.5 rounded-lg font-medium text-sm hover:bg-brand/90 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="loading">{{ $t('Login_Loading') }}</span>
          <span v-else>{{ $t('Login_Button') }}</span>
        </button>

        <div class="text-center text-xs text-gray-500 dark:text-gray-300 pt-2">
          {{ $t('Login_Or') }}
        </div>

        <button
          type="button"
          @click="goToRegister"
          class="w-full border border-brand text-brand py-2.5 rounded-lg font-medium text-sm hover:bg-brand/10 active:scale-[0.98] transition-all duration-200"
        >
          {{ $t('Login_Register_Link') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { showAppNotification } from '@/utils/appNotification'
import { setStorage, getStorage, removeStorage } from '@/utils/storage'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import logger from '@/utils/logger'

const router = useRouter()
const { t } = useI18n()
const api = inject('api')

const email = ref('')
const password = ref('')
const showPassword = ref(false)
const rememberMe = ref(false)
const loading = ref(false)
const emailError = ref('')
const passwordError = ref('')

onMounted(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const savedEmail = getStorage(STORAGE_KEYS.REMEMBER_EMAIL)
  const legacyEmail = getStorage('email')

  // 清理历史脏数据：若 remember_email 里是语言代码(en/zh/zh-HK 等)或其他非邮箱值，删除它
  if (typeof savedEmail === 'string' && savedEmail) {
    if (emailRegex.test(savedEmail)) {
      email.value = savedEmail
      rememberMe.value = true
    } else {
      removeStorage(STORAGE_KEYS.REMEMBER_EMAIL)
    }
  }

  // 兼容旧版本遗留的 email 键：只接受合法邮箱，否则清理
  if (typeof legacyEmail === 'string' && legacyEmail) {
    if (!email.value && emailRegex.test(legacyEmail)) {
      email.value = legacyEmail
      rememberMe.value = true
      setStorage(STORAGE_KEYS.REMEMBER_EMAIL, legacyEmail)
    }
    removeStorage('email')
  }
})

const validateForm = () => {
  emailError.value = ''
  passwordError.value = ''

  if (!email.value) {
    emailError.value = t('Login_Email_Required')
    return false
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email.value)) {
    emailError.value = t('Login_Email_Invalid')
    return false
  }

  if (!password.value) {
    passwordError.value = t('Login_Password_Required')
    return false
  }

  if (password.value.length < 6) {
    passwordError.value = t('Login_Password_Min_Length')
    return false
  }

  return true
}

const handleLogin = async () => {
  if (!validateForm()) {
    return
  }

  loading.value = true
  try {
    const response = await api.auth.login(email.value, password.value)
    
    // 处理响应格式：API 返回的是 { content: { status, data } }
    const responseData = response.data?.content || response.data
    
    if (responseData?.status === '0' && responseData?.data?.access_token) {
      // 保存 token
      setStorage(STORAGE_KEYS.TOKEN, responseData.data.access_token)
      
      // 处理记住我
      if (rememberMe.value) {
        setStorage(STORAGE_KEYS.REMEMBER_EMAIL, email.value)
      } else {
        removeStorage(STORAGE_KEYS.REMEMBER_EMAIL)
      }
      
      showAppNotification({
        message: t('Login_Success'),
        type: 'success',
        duration: 2000,
        position: 'bottom-right'
      })

      // 跳转到首页或之前访问的页面
      const redirect = router.currentRoute.value.query.redirect || '/'
      
      // 如果重定向到需要认证的页面，或者是登录页本身，或者是 orders 页面但用户是从个人中心跳转来的（这里做简化处理）
      // 检查 redirect 是否是公开页面，如果是，则跳转到首页
      // 这里主要解决：如果用户在 orders 页面（需要登录），被重定向到 login?redirect=/orders
      // 登录成功后跳转到 /orders，但如果用户在 /orders 点击返回，可能会因为历史记录问题出现循环
      // 但用户的具体需求是：如果 redirectUrl 是 order，点击返回（可能是浏览器返回？）又回到了登录页面
      // 实际上这里是登录成功后的跳转逻辑。
      // 用户的描述可能是：在 Order 页面 -> 未登录 -> 重定向 Login -> 登录成功 -> 跳转 Order
      // 这时候点击 Order 页面的“返回”按钮。
      // 我们需要检查 Order 页面的返回逻辑。
      
      router.push(redirect)
    } else {
      const errorMsg = responseData?.data?.error || t('Login_Failed')
      showAppNotification({
        message: errorMsg,
        type: 'error',
        duration: 2500,
        position: 'bottom-right'
      })
    }
  } catch (error) {
    // 错误已由 API 拦截器处理
    logger.error('Login error:', error)
  } finally {
    loading.value = false
  }
}

const goToRegister = () => {
  router.push('/register')
}

const goToForgotPassword = () => {
  router.push('/forgot-password')
}
</script>

<style scoped>
/* 确保复选框选中时使用 brand 颜色 */
input[type="checkbox"] {
  accent-color: #8B5CF6 !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border: 2px solid #d1d5db;
  border-radius: 3px;
  background-color: white;
  cursor: pointer;
  position: relative;
}

input[type="checkbox"]:checked {
  background-color: #8B5CF6 !important;
  border-color: #8B5CF6 !important;
}

input[type="checkbox"]:checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

input[type="checkbox"]:checked:focus {
  border-color: #8B5CF6 !important;
  box-shadow: 0 0 0 1px #8B5CF6 !important;
}

input[type="checkbox"]:focus {
  outline: none;
  box-shadow: 0 0 0 1px #8B5CF6;
}
</style>
